/**
 * Fairfax & Favor size-display system.
 *
 * Canonical Shopify variant titles remain unchanged. This module is the single
 * formatter for customer-facing UK / EU / US labels and persists an explicit
 * size-system preference.
 */
(() => {
  const STORAGE_KEY = 'faf:size-system';
  const SYSTEMS = new Set(['UK', 'EU', 'US']);

  const WOMENS_US = {
    'UK 2 EU 35': 'US 4',
    'UK 3 EU 36': 'US 5',
    'UK 4 EU 37': 'US 6',
    'UK 5 EU 38': 'US 7',
    'UK 6 EU 39': 'US 8',
    'UK 7 EU 40': 'US 9',
    'UK 7.5 EU 41': 'US 9.5',
    'UK 8 EU 42': 'US 10',
    'UK 9 EU 43': 'US 11',
    'UK 10 EU 44': 'US 12',
    'UK 3 - 6': 'US 5 - 8',
    'UK 7 - 9': 'US 9 - 11'
  };

  const MENS_US = {
    'UK 6 EU 40': 'US 7',
    'UK 7 EU 41': 'US 8',
    'UK 8 EU 42': 'US 9',
    'UK 9 EU 43': 'US 10',
    'UK 10 EU 44': 'US 11',
    'UK 11 EU 45': 'US 12',
    'UK 12 EU 46': 'US 13',
    'UK 13 EU 47': 'US 14',
    'UK 14 EU 48': 'US 15'
  };

  const WOMENS_APPAREL_US = {
    'UK 6': 'US 2',
    'UK 8': 'US 4',
    'UK 10': 'US 6',
    'UK 12': 'US 8',
    'UK 14': 'US 10',
    'UK 16': 'US 12',
    'UK 18': 'US 14',
    'UK 8 EU 36': 'US 4',
    'UK 10 EU 38': 'US 6',
    'UK 12 EU 40': 'US 8',
    'UK 14 EU 42': 'US 10',
    'UK 16 EU 44': 'US 12',
    'UK 18 EU 46': 'US 14'
  };

  const WOMENS_APPAREL_EU = {
    'UK 6': 'EU 34',
    'UK 8': 'EU 36',
    'UK 10': 'EU 38',
    'UK 12': 'EU 40',
    'UK 14': 'EU 42',
    'UK 16': 'EU 44',
    'UK 18': 'EU 46'
  };

  const WOMENS_FOOTWEAR_EU = {
    'UK 2': 'EU 35',
    'UK 3': 'EU 36',
    'UK 4': 'EU 37',
    'UK 5': 'EU 38',
    'UK 6': 'EU 39',
    'UK 7': 'EU 40',
    'UK 7.5': 'EU 41',
    'UK 8': 'EU 42',
    'UK 9': 'EU 43',
    'UK 10': 'EU 44'
  };

  const WOMENS_FOOTWEAR_US = {
    'UK 2': 'US 4',
    'UK 3': 'US 5',
    'UK 4': 'US 6',
    'UK 5': 'US 7',
    'UK 6': 'US 8',
    'UK 7': 'US 9',
    'UK 7.5': 'US 9.5',
    'UK 8': 'US 10',
    'UK 9': 'US 11',
    'UK 10': 'US 12'
  };

  const MENS_FOOTWEAR_EU = {
    'UK 6': 'EU 40',
    'UK 7': 'EU 41',
    'UK 8': 'EU 42',
    'UK 9': 'EU 43',
    'UK 10': 'EU 44',
    'UK 11': 'EU 45',
    'UK 12': 'EU 46',
    'UK 13': 'EU 47',
    'UK 14': 'EU 48'
  };

  const MENS_FOOTWEAR_US = {
    'UK 6': 'US 7',
    'UK 7': 'US 8',
    'UK 8': 'US 9',
    'UK 9': 'US 10',
    'UK 10': 'US 11',
    'UK 11': 'US 12',
    'UK 12': 'US 13',
    'UK 13': 'US 14',
    'UK 14': 'US 15'
  };

  const EUROPEAN_COUNTRIES = new Set([
    'AL', 'AD', 'AT', 'BE', 'BG', 'BA', 'HR', 'CY', 'CZ', 'DK', 'EE',
    'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT',
    'LU', 'MT', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO', 'SM',
    'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'TR', 'VA'
  ]);

  const normaliseSystem = (value) => {
    const system = String(value || '').trim().toUpperCase();
    return SYSTEMS.has(system) ? system : '';
  };

  const normaliseGender = (value) => {
    const gender = String(value || '').trim().toLowerCase();

    if (['women', 'womens', "women's", 'female'].includes(gender)) return 'womens';
    if (['men', 'mens', "men's", 'male'].includes(gender)) return 'mens';

    return '';
  };

  const normaliseCategory = (value) => {
    const category = String(value || '').trim().toLowerCase();

    if (category.includes('apparel') || category.includes('clothing')) return 'apparel';
    if (
      category.includes('footwear') ||
      category.includes('boot') ||
      category.includes('shoe') ||
      category.includes('sandal') ||
      category.includes('trainer')
    ) {
      return 'footwear';
    }

    return '';
  };

  const countryDefault = () => {
    const country = String(
      window.FAFThemeConfig?.countryCode ||
      window.Shopify?.country ||
      ''
    ).toUpperCase();

    if (country === 'US' || country === 'CA') return 'US';
    if (EUROPEAN_COUNTRIES.has(country)) return 'EU';
    return 'UK';
  };

  const readPreference = () => {
    try {
      return normaliseSystem(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return '';
    }
  };

  const readAccountPreference = () => {
    const node = document.getElementById('faf-customer-fit');
    if (!node) return '';

    try {
      const profile = JSON.parse(node.textContent || '{}');
      return normaliseSystem(profile?.preferredSizeSystem);
    } catch (error) {
      return '';
    }
  };

  const writePreference = (system) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, system);
    } catch (error) {
      // Local storage is an enhancement only.
    }
  };

  const ukPart = (rawSize) => {
    const match = String(rawSize || '').trim().match(/^UK\s+([0-9.]+)(?=\s|$)/i);
    return match ? `UK ${match[1]}` : '';
  };

  const ukNumber = (rawSize) => {
    const match = String(rawSize || '').trim().match(/^UK\s+([0-9.]+)(?=\s|$)/i);
    if (!match) return null;

    const value = Number(match[1]);
    return Number.isFinite(value) ? value : null;
  };

  const displayNumber = (value) => (
    Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)))
  );

  const euPart = (rawSize) => {
    const match = String(rawSize || '').trim().match(/\bEU\s+([0-9.]+)/i);
    return match ? `EU ${match[1]}` : '';
  };

  const footwearFallback = (rawSize, gender, system) => {
    const uk = ukNumber(rawSize);
    if (uk === null) return '';

    if (system === 'EU') {
      if (gender === 'womens') return `EU ${displayNumber(uk + 33)}`;
      if (gender === 'mens') return `EU ${displayNumber(uk + 34)}`;
    }

    if (system === 'US') {
      if (gender === 'womens') return `US ${displayNumber(uk + 2)}`;
      if (gender === 'mens') return `US ${displayNumber(uk + 1)}`;
    }

    return '';
  };

  const inferCategory = (rawSize, gender, category) => {
    if (category) return category;

    const raw = String(rawSize || '').trim();

    if (/^UK\s+[0-9.]+\s+EU\s+[0-9.]+$/i.test(raw)) return 'footwear';

    if (gender === 'womens' && Object.prototype.hasOwnProperty.call(WOMENS_APPAREL_US, raw)) return 'apparel';

    return 'footwear';
  };

  const format = (rawSize, context = {}) => {
    const raw = String(rawSize ?? '').trim();
    if (!raw) return '';

    const system = normaliseSystem(context.preference) || API.preference;
    const gender = normaliseGender(context.gender);
    const suppliedCategory = normaliseCategory(context.category);
    const category = inferCategory(raw, gender, suppliedCategory);

    if (!/^UK\s/i.test(raw)) return raw;

    if (system === 'UK') {
      return ukPart(raw) || raw;
    }

    if (system === 'EU') {
      const canonicalEu = euPart(raw);
      if (canonicalEu) return canonicalEu;

      if (category === 'apparel' && gender !== 'mens') {
        return WOMENS_APPAREL_EU[raw] || raw;
      }

      const uk = ukPart(raw);
      if (!uk) return raw;

      if (gender === 'mens') return MENS_FOOTWEAR_EU[uk] || footwearFallback(raw, gender, system) || raw;
      if (gender === 'womens') return WOMENS_FOOTWEAR_EU[uk] || footwearFallback(raw, gender, system) || raw;

      const womens = WOMENS_FOOTWEAR_EU[uk];
      const mens = MENS_FOOTWEAR_EU[uk];
      return womens && mens && womens === mens ? womens : raw;
    }

    if (system === 'US') {
      if (category === 'apparel' && gender !== 'mens') {
        return WOMENS_APPAREL_US[raw] || raw;
      }

      if (gender === 'womens') {
        return WOMENS_US[raw] || WOMENS_FOOTWEAR_US[ukPart(raw)] || footwearFallback(raw, gender, system) || raw;
      }

      if (gender === 'mens') {
        return MENS_US[raw] || MENS_FOOTWEAR_US[ukPart(raw)] || footwearFallback(raw, gender, system) || raw;
      }

      const womens = WOMENS_US[raw] || WOMENS_FOOTWEAR_US[ukPart(raw)];
      const mens = MENS_US[raw] || MENS_FOOTWEAR_US[ukPart(raw)];

      if (womens && mens && womens !== mens) return raw;
      return womens || mens || raw;
    }

    return raw;
  };

  const nodeContext = (node) => ({
    gender:
      node.dataset.sizeGender ||
      node.dataset.gender ||
      node.closest('[data-size-gender]')?.dataset.sizeGender ||
      '',
    category:
      node.dataset.sizeCategory ||
      node.dataset.category ||
      node.closest('[data-size-category]')?.dataset.sizeCategory ||
      ''
  });

  const applyNode = (node) => {
    const raw = node.dataset.rawSize || node.dataset.sizeLabel || '';
    if (!raw) return;

    const label = format(raw, nodeContext(node));
    const output = node.querySelector('[data-size-output]') || node;

    if (output.textContent !== label) {
      output.textContent = label;
    }

    if (node.hasAttribute('data-size-update-aria')) {
      const suffix = node.dataset.sizeAriaSuffix || '';
      node.setAttribute('aria-label', `${label}${suffix}`);
    }
  };

  const apply = (root = document) => {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const nodes = [];

    if (scope instanceof Element && scope.matches('[data-size-label], [data-raw-size]')) {
      nodes.push(scope);
    }

    nodes.push(...scope.querySelectorAll('[data-size-label], [data-raw-size]'));
    nodes.forEach(applyNode);
  };

  const storedPreference = readPreference();
  const accountPreference = readAccountPreference();

  const initialPreference =
    storedPreference ||
    accountPreference ||
    countryDefault();

  const API = {
    preference: initialPreference,
    format,
    apply,
    set(value) {
      const system = normaliseSystem(value);
      if (!system || system === this.preference) return;

      this.preference = system;
      writePreference(system);

      const alpineStore = window.Alpine?.store?.('sizeSystem');
      if (alpineStore && alpineStore.preference !== system) {
        alpineStore.preference = system;
      }

      apply(document);

      window.dispatchEvent(new CustomEvent('faf:size-preference-changed', {
        detail: { system }
      }));
    },
    is(value) {
      return this.preference === normaliseSystem(value);
    }
  };

  window.FAFSizes = API;

  window.translateSize = (rawSize, gender, category = '') => (
    API.format(rawSize, { gender, category })
  );

  window.translateRawSizeLabels = (root = document) => API.apply(root);

  document.addEventListener('alpine:init', () => {
    Alpine.store('sizeSystem', {
      preference: API.preference,
      set(value) {
        API.set(value);
        this.preference = API.preference;
      },
      is(value) {
        return this.preference === normaliseSystem(value);
      },
      format(rawSize, context = {}) {
        return API.format(rawSize, {
          ...context,
          preference: context.preference || this.preference
        });
      },
      apply(root = document) {
        API.apply(root);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => apply(document), { once: true });
  } else {
    apply(document);
  }

  window.addEventListener('faf:section-rendered', (event) => {
    apply(event.detail?.root || document);
  });

  window.addEventListener('lazy-panel:ready', (event) => {
    const panel = String(event.detail?.panel || '');
    if (!panel) return;

    const root = document.getElementById(`lazy-${panel}`);
    if (root) apply(root);
  });
})();
